import { getAuth } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import config from '../config';
import { computeCanonicalHash } from '../utils/hash';
import { isSystemStackPinned, SYSTEM_STACK_TYPES } from './systemStackPrefs';

/**
 * Service for managing diet support stack generation
 */
export class DietStackService {
  static normalizeKeyFromIdOrName(supplementId, name) {
    // Try to transform 'vitamin-b12' -> 'VitaminB12', and also 'Vitamin B12' -> 'VitaminB12'
    if (supplementId && typeof supplementId === 'string') {
      const fromId = supplementId.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\s+/g, '');
      if (fromId) return fromId;
    }
    if (name && typeof name === 'string') {
      const fromName = name.replace(/\s+/g, '');
      if (fromName) return fromName;
    }
    return null;
  }

  static async fetchDietNotes(version = 'v1') {
    try {
      // FIREBASE_STORAGE_BASE_URL already ends with '/o/'
      const base = config.FIREBASE_STORAGE_BASE_URL || '';
      const objectPath = encodeURIComponent(`configs/diet_notes/${version}/diet_notes.json`);
      const url = `${base}${objectPath}?alt=media`;
      const res = await fetch(url, { method: 'GET' });
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      console.warn('[DietStackService] Failed to fetch diet notes:', e);
      return null;
    }
  }

  static async overrideNotesForStack(stack, dietPreference, version = 'v1') {
    try {
      if (!stack || !Array.isArray(stack.supplements) || !dietPreference) return stack;
      const notesJson = await this.fetchDietNotes(version);
      if (!notesJson || !notesJson[dietPreference]) return stack;
      const notesForDiet = notesJson[dietPreference];
      const updatedSupps = stack.supplements.map(item => {
        const keyGuess = this.normalizeKeyFromIdOrName(item.supplementId, item.name);
        const note = (keyGuess && notesForDiet[keyGuess]) || item.note;
        return note ? { ...item, note } : item;
      });
      return { ...stack, supplements: updatedSupps };
    } catch (e) {
      console.warn('[DietStackService] overrideNotesForStack failed:', e);
      return stack;
    }
  }
  
  /**
   * Generate diet support stack
   * @param {Object} userProfile - User profile data (age, sex, lifestage, diet)
   * @returns {Promise<boolean>} - Success status
   */
  static async generateStack(userProfile) {
    try {
      const user = getAuth().currentUser;
      if (!user) {
        console.warn('No authenticated user for diet stack generation');
        return false;
      }

      const authToken = await user.getIdToken();
      
      // Compute profile hash (latest-wins gate)
      const profile_hash = await computeCanonicalHash({
        age: userProfile?.age,
        sex: userProfile?.sex,
        lifestage: userProfile?.lifestage || {},
        diet: userProfile?.diet
      });

      // Make API call to generate diet support stack
      const response = await fetch(`${config.BASE_URL}/diet-support-stack`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ ...userProfile, profile_hash })
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('❌ [DietStackService] API error:', errorData);
        return false;
      }

      console.log('🚀 [DietStackService] Diet stack generation triggered successfully');
      return true;
    } catch (error) {
      console.error('❌ [DietStackService] Failed to generate diet support stack:', error);
      return false;
    }
  }

  /**
   * Get diet support stack info
   * @returns {Promise<{stack: Object|null, updateTime: Date|null}>}
   */
  static async getDietStackInfo() {
    try {
      const user = getAuth().currentUser;
      if (!user) return { stack: null, updateTime: null };

      // Check for diet support stack in system_stacks sub-collection
      const systemStackDoc = await getDoc(doc(db, 'users', user.uid, 'system_stacks', 'diet_support'));
      if (!systemStackDoc.exists()) {
        console.log('🔍 [DietStackService] No diet stack document found');
        return { stack: null, updateTime: null };
      }

      const data = systemStackDoc.data();
      if (!data || !data.stackData) {
        console.log('🔍 [DietStackService] No stackData found in diet stack document');
        return { stack: null, updateTime: null };
      }

      const updateTime = data.updatedAt?.toDate() || new Date();

      // One read of users/{uid}, used for both the pin and the note override.
      // The pin lives there now (STACK-526); `data.isPinned` is only the legacy
      // location the fallback still honours.
      let userData = null;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        userData = userDoc.exists() ? userDoc.data() : null;
      } catch (e) {
        console.warn('⚠️ [DietStackService] Could not read user document; falling back to the stack document for the pin');
      }

      const stack = {
        id: `system_diet_support_${user.uid}`,
        ...data.stackData,
        isSystemGenerated: true,
        systemStackType: 'diet_support',
        isPinned: isSystemStackPinned(userData, SYSTEM_STACK_TYPES.DIET, data),
        jsCreatedAt: data.createdAt?.toDate() || new Date(),
        jsUpdatedAt: updateTime,
        sourceProfileHash: data.sourceProfileHash || null
      };

      // Override notes dynamically from Storage if available
      try {
        const diet = userData?.dietPreference;
        const overridden = await DietStackService.overrideNotesForStack(stack, diet, 'v1');
        Object.assign(stack, overridden);
      } catch (e) {
        console.warn('[DietStackService] Note override skipped:', e);
      }

      console.log('🔍 [DietStackService] Found diet stack with update time:', updateTime);
      return { stack, updateTime };
    } catch (error) {
      console.error('❌ [DietStackService] Failed to get diet stack info:', error);
      return { stack: null, updateTime: null };
    }
  }

  /**
   * Check if a diet support stack exists
   * @returns {Promise<boolean>} - True if stack exists
   */
  static async stackExists() {
    try {
      const { stack } = await this.getDietStackInfo();
      return !!stack;
    } catch (error) {
      console.error('❌ [DietStackService] Failed to check if diet stack exists:', error);
      return false;
    }
  }

  /**
   * Get user profile data from Firestore
   * @returns {Promise<Object|null>} - User profile data or null
   */
  static async getUserProfile() {
    try {
      const user = getAuth().currentUser;
      if (!user) return null;

      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (!userDoc.exists()) return null;

      return userDoc.data();
    } catch (error) {
      console.error('❌ [DietStackService] Failed to get user profile:', error);
      return null;
    }
  }
}